import React from 'react';
import { Box, Container, Typography, Toolbar } from '@mui/material';
import FooterSection from '../components/FooterSection';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Typography
      component="h2"
      sx={{
        fontFamily: 'Outfit',
        fontWeight: 700,
        fontSize: '1.25rem',
        color: '#000000',
        mb: 1.5,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const Para = ({ children }) => (
  <Typography
    sx={{
      fontFamily: 'Outfit',
      fontWeight: 400,
      fontSize: '0.95rem',
      color: '#555555',
      lineHeight: 1.85,
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

const PrivacyPolicyPage = () => {
  return (
    <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <Toolbar sx={{ height: '68px' }} />

      {/* Page hero */}
      <Box sx={{ backgroundColor: '#000000', py: { xs: 8, md: 12 }, borderBottom: '1px solid #1a1a1a' }}>
        <Container maxWidth="lg">
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              color: '#ffffff',
              letterSpacing: '-0.02em',
              mb: 2,
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 300,
              fontSize: '1.15rem',
              color: 'rgba(255,255,255,0.6)',
              maxWidth: 520,
            }}
          >
            How Sigmatiqa Ltd collects, uses, and protects your information.
          </Typography>
        </Container>
      </Box>

      {/* Policy content */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          <Para>
            Last updated: September 2026
          </Para>

          <Section title="1. Who we are">
            <Para>
              Sigmatiqa Ltd ("we", "us", "our") is a company registered in England and Wales. We
              provide temperature monitoring hardware and a cloud-based IoT platform that helps
              organisations achieve compliance, reduce labour and costs, and receive early failure
              indications.
            </Para>
          </Section>

          <Section title="2. What personal data we collect">
            <Para>
              We collect only the minimum personal data necessary to operate our service:
            </Para>
            <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>
              <Box component="li" sx={{ fontFamily: 'Outfit', fontSize: '0.95rem', color: '#555555', lineHeight: 1.85, mb: 0.5 }}>
                <strong>Account credentials</strong> - your email address and a password you choose when registering for the dashboard.
              </Box>
              <Box component="li" sx={{ fontFamily: 'Outfit', fontSize: '0.95rem', color: '#555555', lineHeight: 1.85, mb: 0.5 }}>
                <strong>Site address data</strong> - the physical addresses of sites you add to the dashboard for monitoring purposes.
              </Box>
            </Box>
            <Para>
              We do not collect or store any other personal data. All remaining data held in our
              cloud infrastructure relates exclusively to sensor readings and device telemetry.
            </Para>
          </Section>

          <Section title="3. How we use your data">
            <Para>
              We use the personal data we collect for the following purposes:
            </Para>
            <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>
              <Box component="li" sx={{ fontFamily: 'Outfit', fontSize: '0.95rem', color: '#555555', lineHeight: 1.85, mb: 0.5 }}>
                To authenticate your access to the Sigmatiqa dashboard.
              </Box>
              <Box component="li" sx={{ fontFamily: 'Outfit', fontSize: '0.95rem', color: '#555555', lineHeight: 1.85, mb: 0.5 }}>
                To associate sensors and monitoring data with the correct site locations.
              </Box>
              <Box component="li" sx={{ fontFamily: 'Outfit', fontSize: '0.95rem', color: '#555555', lineHeight: 1.85, mb: 0.5 }}>
                To send you service-related communications such as alert notifications and account updates.
              </Box>
            </Box>
            <Para>
              We will never sell, rent, or share your personal data with third parties for marketing
              purposes.
            </Para>
          </Section>

          <Section title="4. Legal basis for processing">
            <Para>
              We process your personal data on the basis of contractual necessity - it is required to
              provide you with access to and use of our platform. Where we send service-related
              communications, this is in our legitimate interest to keep you informed about your
              monitoring system.
            </Para>
          </Section>

          <Section title="5. Data storage and security">
            <Para>
              All data is stored securely in UK-based data centres. We use encryption in transit and
              at rest to protect your information. Access to personal data is restricted to authorised
              personnel only.
            </Para>
            <Para>
              Sensor and device data is retained for up to 5 years. Account data is retained for the
              duration of your subscription and deleted upon request following account closure.
            </Para>
          </Section>

          <Section title="6. Data sharing">
            <Para>
              We do not share your personal data with third parties except where necessary to operate
              our service (for example, cloud hosting providers) or where required by law. Any
              third-party service providers we use are contractually bound to protect your data and
              process it only on our instructions.
            </Para>
          </Section>

          <Section title="7. Your rights">
            <Para>
              Under UK GDPR, you have the right to:
            </Para>
            <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>
              <Box component="li" sx={{ fontFamily: 'Outfit', fontSize: '0.95rem', color: '#555555', lineHeight: 1.85, mb: 0.5 }}>
                Access the personal data we hold about you.
              </Box>
              <Box component="li" sx={{ fontFamily: 'Outfit', fontSize: '0.95rem', color: '#555555', lineHeight: 1.85, mb: 0.5 }}>
                Request correction of inaccurate data.
              </Box>
              <Box component="li" sx={{ fontFamily: 'Outfit', fontSize: '0.95rem', color: '#555555', lineHeight: 1.85, mb: 0.5 }}>
                Request deletion of your data.
              </Box>
              <Box component="li" sx={{ fontFamily: 'Outfit', fontSize: '0.95rem', color: '#555555', lineHeight: 1.85, mb: 0.5 }}>
                Object to or restrict processing.
              </Box>
              <Box component="li" sx={{ fontFamily: 'Outfit', fontSize: '0.95rem', color: '#555555', lineHeight: 1.85, mb: 0.5 }}>
                Request data portability.
              </Box>
            </Box>
            <Para>
              To exercise any of these rights, please contact us at privacy@sigmatiqa.com.
            </Para>
          </Section>

          <Section title="8. Cookies and analytics">
            <Para>
              We use cookies and similar technologies on this website to help us understand how
              visitors interact with our content. Specifically, we use Google Analytics to collect
              anonymised information about pages visited, time spent on the site, and general usage
              patterns. This data is used solely to improve our website and the products we offer -
              we do not use it for advertising or to build profiles about individual users.
            </Para>
            <Para>
              Google Analytics processes your IP address to derive approximate geographic location
              (typically to city or regional level). Your full IP address is not stored by Google
              Analytics after this processing step. Under UK GDPR, IP addresses are classified as
              personal data; we therefore only enable this processing after you have given your
              consent via the cookie banner on this site.
            </Para>
            <Para>
              Under UK GDPR and the Privacy and Electronic Communications Regulations (PECR), we ask
              for your consent before placing analytics cookies on your device. You can accept or
              decline analytics cookies when you first visit the site. If you decline, no analytics
              data will be collected and no cookies will be set. You can change your preference at
              any time by clearing your browser's local storage for this site.
            </Para>
            <Para>
              Our dashboard uses essential cookies required for authentication and session
              management. These are strictly necessary for the service to function and do not require
              your consent.
            </Para>
          </Section>

          <Section title="9. Changes to this policy">
            <Para>
              We may update this privacy policy from time to time. Any changes will be posted on this
              page with an updated revision date. We encourage you to review this policy periodically.
            </Para>
          </Section>

          <Section title="10. Contact us">
            <Para>
              If you have any questions about this privacy policy or how we handle your data, please
              contact us at:
            </Para>
            <Para>
              Sigmatiqa Ltd
              <br />
              Email: privacy@sigmatiqa.com
            </Para>
          </Section>
        </Container>
      </Box>

      <FooterSection />
    </Box>
  );
};

export default PrivacyPolicyPage;
